import React, { useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { useSiteConfig } from "components/Site/Contexts/SiteConfig";
import { useUserSettings } from "components/Site/Contexts/UserSettings";
import {
  lighten,
  saturate,
  getLuminance,
  meetsContrastGuidelines,
  darken
} from "polished";

import { useBible } from "components/Bible/BibleContainer";
import AudioController from "../ChapterAudio/AudioController";
import { isMobileDevice } from "../../../utils/mobile";
import { CHAPTER_MODES } from "./ChapterMode";
import { ViewSettingsMenu } from "../../UI/ViewSettingsMenu";
import Button from "components/UI/Button";
import { useServiceWorker } from "../../Site/ServiceWorkerContext";
import RefreshIcon from "icons/RefreshIcon";
import LocationTargetIcon from "icons/LocationTargetIcon";

// ignore pointer events on the element, but enable for children
const pointerEventStyles = css`
  pointer-events: none;

  & * {
    pointer-events: all;
  }
`;

const getUpdateBgColor = (color: string) =>
  saturate(
    0.1,
    getLuminance(color) > 0.5 ? darken(0.2, color) : lighten(0.1, color)
  );

const getUpdateTextColor = (color: string) =>
  meetsContrastGuidelines("white", color).AA ? "white" : "black";

const fadeSlideIn = keyframes`
  0% { transform: translateY(100%); opacity: 0; }
  80% { opacity: 1; }
  100% { transform: translateY(0%); opacity: 1; }
`;

const spin = keyframes`
  to { transform: rotate(180deg); }
`;

const UpdateServiceWorkerButton = styled.button`
  background: ${({ theme }) => getUpdateBgColor(theme.primary)};
  color: ${({ theme }) => getUpdateTextColor(getUpdateBgColor(theme.primary))};
  border-radius: 4px;
  padding: 14px 16px;
  border: none;
  outline: none;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-family: "Noto Sans", sans-serif;
  line-height: 1;
  transition: all 150ms ease, box-shadow 400ms ease;
  animation: ${fadeSlideIn} 1s cubic-bezier(0.785, 0.135, 0.15, 0.86) 1;

  &:disabled {
    svg {
      transform: rotate(0deg);
      animation: ${spin} 700ms cubic-bezier(0.645, 0.045, 0.355, 1) infinite;
    }
  }

  &:hover {
    cursor: pointer;
    box-shadow: 1px 5px 10px rgba(0, 0, 0, 0.1);
    transform: scale(1.05);
  }
`;

const StyledFooter = styled.div`
  position: fixed;
  width: 100%;
  bottom: 0;
  left: 0;
  z-index: 1;
  ${pointerEventStyles}
`;

const IconsContainer = styled.div`
  display: flex;
  flex-direction: column;

  & > :not(:last-child) {
    margin-bottom: 16px;
  }
`;

const ViewSettingsMenuButton = styled.button`
  background: none;
  outline: none;
  border: none;
  padding: 0;
`;

const SettingsContainer = styled.div<{ anchor: string }>`
  padding: 16px;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: ${props =>
    props.anchor === "right" ? "row-reverse" : "row"};
  justify-content: space-between;
  align-items: flex-end;
  ${pointerEventStyles}
`;

interface ChapterFooterProps {
  audioIsPlayable: boolean;
  mode: string;
  setModeDefault: () => void;
  setModeAudioSync: () => void;
}

export const ChapterFooter: React.FC<ChapterFooterProps> = ({
  audioIsPlayable,
  mode,
  setModeDefault,
  setModeAudioSync
}) => {
  const [viewSettingsOpen, setViewSettingsOpen] = useState(false);
  const { complete } = useBible();
  const { scriptDirection, includeAllBibles } = useSiteConfig();
  const { darkMode } = useUserSettings();

  const allBiblesLoaded = includeAllBibles ? complete : true;
  const anchor = scriptDirection === "RTL" ? "right" : "left";
  const isMobile = isMobileDevice();
  const isAudioUserScrollingMode = mode === CHAPTER_MODES.AUDIO_USER_SCROLLING;
  const showSettings = !isMobile && !isAudioUserScrollingMode;
  const sw = useServiceWorker();

  return (
    <StyledFooter>
      <SettingsContainer anchor={anchor}>
        <div>
          {showSettings && (
            <IconsContainer>
              <ViewSettingsMenuButton
                type="button"
                onClick={() => setViewSettingsOpen(!viewSettingsOpen)}
              >
                <ViewSettingsMenu
                  anchor={anchor}
                  iconColor={darkMode ? "white" : "black"}
                  open={viewSettingsOpen}
                />
              </ViewSettingsMenuButton>
            </IconsContainer>
          )}
        </div>
        <div>
          <IconsContainer>
            {sw.hasUpdate && (
              <UpdateServiceWorkerButton
                type="button"
                onClick={sw.handleUpdate}
                disabled={sw.isUpdating}
              >
                <RefreshIcon style={{ fontSize: 18 }} />
              </UpdateServiceWorkerButton>
            )}
            {isAudioUserScrollingMode && (
              <Button
                renderIcon={(iconProps: any) => (
                  <LocationTargetIcon {...iconProps} />
                )}
                onClick={setModeAudioSync}
                type="circle"
              />
            )}
          </IconsContainer>
        </div>
      </SettingsContainer>
      {allBiblesLoaded && audioIsPlayable && (
        <AudioController
          setModeDefault={setModeDefault}
          setModeAudioSync={setModeAudioSync}
        />
      )}
    </StyledFooter>
  );
};
